import { useState } from 'react';
import useOnlyDevelopers from '../../../../../../../../state/setOnlyDevelopers';
import { AxiosError } from 'axios';
import { ExternalIcon, InternalIcon } from 'assets/images/bussines';
import { MinEditIcon } from '../../../../../../../../assets/images/blogForm';
import { NudosCareTag, NudosHoverText, NudosSelectButton } from '../../../../../../../../components/NudosComponents';
import NudosProductPills from '../../../../../../../../components/NudosComponents/NudosProductPills/NudosProductPills';
import { REFERENCE_MODEL_TYPE, TnudosCare } from '../../../../../../../../types/global';
import {
  ISerialAndSKU,
  IlogisticServiceDetails,
  IlogisticServiceDetailsOrganizationData,
  IlogisticServiceDetailsToolsData,
  LOCATION_PLACE_TYPE,
  LOGISTIC_SERVICE_STATUS_NAME
} from '../../../../../../../../types/services';
import { EditToolsDataModal } from '../../EditToolsDataModal';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import { updateDataSerialAndSKU } from '../../../../../../../../services/services';
import ChangeToolsModal from '../../ChangeToolsModal/ChangeToolsModal';
import { IProductInfo } from '../../../../../../../../types/products';
import { QRDownloadButton } from '../../../../../../../../components';
import BordOneToneIcon from '../../../../../../../../components/NudosComponents/BordOneToneIcon/BordOneToneIcon';
import useLogisticServicesState from 'state/useLogisticServicesState';
import './LsToolCardToolDataSection.scss';

const LsToolCardToolDataSection = ({
  toolData,
  updateDataCallback,
  organizationInfo,
  logisticServiceData
}: {
  toolData: IlogisticServiceDetailsToolsData;
  updateDataCallback: () => Promise<void>;
  logisticServiceData?: IlogisticServiceDetails | null;
  organizationInfo?: IlogisticServiceDetailsOrganizationData | null | undefined;
}) => {
  const { onlyDevelopers } = useOnlyDevelopers();
  const { toolsSelected, setToolsSelected } = useLogisticServicesState();
  const { name, pills, productId, referenceModel, nudosCare, serial, sku, isBuyAndHold, isGeneric } = toolData;
  const { originLocation, destinationLocation, statusName } = logisticServiceData || {};
  const [showEditDataModal, setShowEditDataModal] = useState(false);
  const [showChangeToolModal, setShowChangeToolModal] = useState(false);

  const QRData = {
    serial,
    orgName: organizationInfo?.organizationName,
    productId: productId
  };

  const isExternalProduct = referenceModel === REFERENCE_MODEL_TYPE.EXTERNAL;
  const toolModelIcon = isExternalProduct ? <ExternalIcon fill="#999999" /> : <InternalIcon />;

  const noDataText = 'Sin Datos';

  const validOriginForToolReplacement = [LOCATION_PLACE_TYPE.NUDOS].includes(
    originLocation?.place as LOCATION_PLACE_TYPE
  );
  const validDestinationForToolReplacement = [LOCATION_PLACE_TYPE.USER].includes(
    destinationLocation?.place as LOCATION_PLACE_TYPE
  );
  const invalidStatusForToolReplacement = [
    LOGISTIC_SERVICE_STATUS_NAME.CANCELLED,
    LOGISTIC_SERVICE_STATUS_NAME.DELIVERED
  ].includes(statusName as LOGISTIC_SERVICE_STATUS_NAME);

  const canReplaceTool =
    validOriginForToolReplacement && validDestinationForToolReplacement && !invalidStatusForToolReplacement;

  const closeEditDataModal = () => setShowEditDataModal(false);
  const openEditDataModal = () => setShowEditDataModal(true);
  const handleShowChangeToolModal = () => setShowChangeToolModal(prevState => !prevState);

  const updateSerialAndSKU = async (
    serialAndSKU?: ISerialAndSKU,
    loadingHandler?: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    if (!productId) return;
    try {
      loadingHandler && loadingHandler(true);
      await updateDataSerialAndSKU(
        +productId,
        { newSerial: serialAndSKU?.serial, newSku: serialAndSKU?.sku },
        logisticServiceData?.id
      );
      await updateDataCallback();
      closeEditDataModal();
    } catch (error) {
      const err = error as AxiosError;
      if (err?.response?.data?.message?.toLowerCase().includes('serial')) {
        displayErrorNotification({ customJSXMessage: <>El serial no esta disponible intenta con otro</> });
      } else if (err?.response?.data?.message === 'sku not found') {
        displayErrorNotification({ customJSXMessage: <>no encontramos el SKU en la base de datos</> });
      } else {
        displayErrorNotification();
      }
    } finally {
      loadingHandler && loadingHandler(false);
    }
  };

  const handleCheckSerialButton = () => {
    if (!serial || !productId) return;

    const isSelected = toolsSelected?.some(item => item.serial === serial);

    if (isSelected) {
      const newItems = toolsSelected?.filter(item => item.serial !== serial) ?? [];
      setToolsSelected(newItems.length === 0 ? null : newItems);
    } else {
      const currentItems = toolsSelected ?? [];
      setToolsSelected([
        ...currentItems,
        {
          serial,
          productId
        }
      ]);
    }
  };

  return (
    <div className="lsToolCardToolDataSection">
      {showChangeToolModal && (
        <ChangeToolsModal
          updateDataCallback={updateDataCallback}
          productInfo={toolData as IProductInfo}
          organizationInfo={organizationInfo as IlogisticServiceDetailsOrganizationData}
          changeShowModal={handleShowChangeToolModal}
          originalLocation={originLocation}
        />
      )}
      {showEditDataModal && (
        <EditToolsDataModal
          toolData={toolData}
          closeEditDataModal={closeEditDataModal}
          updateSerialAndSKU={updateSerialAndSKU}
        />
      )}
      <div className="firstLine">
        <div className="toolNameContainer">
          <NudosSelectButton
            iconName={
              toolsSelected?.some(
                (item: Pick<IlogisticServiceDetailsToolsData, 'serial' | 'productId'>) => item.serial === serial
              )
                ? 'checkmark'
                : ''
            }
            isButtonActive={toolsSelected?.some(
              (item: Pick<IlogisticServiceDetailsToolsData, 'serial' | 'productId'>) => item.serial === serial
            )}
            handleClickButton={handleCheckSerialButton}
            componentSize="xs w-8 h-8"
          />
          <NudosHoverText onlyIfTruncated customClassName="toolName" text={name || ''} charactersLimit={50} />
        </div>
        <NudosProductPills pills={pills || {}} customHeight={14} />
        {toolModelIcon}
        <NudosCareTag nudosCareType={(nudosCare as TnudosCare) || 'Free'} customHeight="18px" />
      </div>
      <div className="secondLine">
        <QRDownloadButton QRData={QRData} />
        <div className="secondLineField">
          {isBuyAndHold && <div className="buyAndHold">Buy & Hold</div>}
          <div className="title">Serial:</div>
          <div className="value">{serial || noDataText}</div>
        </div>
        <div className="secondLineField">
          <div className="title">SKU:</div>
          <div className="value">{sku || noDataText}</div>
        </div>
        {(canReplaceTool || onlyDevelopers) && (
          <span className="changeToolsText" onClick={handleShowChangeToolModal}>
            Cambiar herramienta
          </span>
        )}
        {isExternalProduct && (
          <div className="editIcon" onClick={openEditDataModal}>
            <MinEditIcon />
          </div>
        )}
      </div>
      {isGeneric && (
        <div className="isGenericSerialWarning">
          <BordOneToneIcon variant="alertTriangleWarning" customWidth="1.5rem" stroke="currentColor" />
          <p>Serial creado automáticamente, actualizar al llegar a Bodegas</p>
        </div>
      )}
    </div>
  );
};

export default LsToolCardToolDataSection;
